import PropTypes from 'prop-types'
import { Fragment, useState, useEffect } from 'react'
import { Helmet } from 'react-helmet'
import { Link } from 'react-router-dom'

import { isTerminalState } from 'components/alfi/alfi-utils'
import AlfiAttack from 'components/alfi/details/AlfiAttack'
import ApiTrigger from 'components/common/ApiTrigger'
import Button from 'components/common/Button'
import Section from 'components/common/Section'
import AttacksLayout from 'components/layouts/AttacksLayout'
import Header, {
  Heading,
  HeaderLink,
  HeaderRightChevron
} from 'components/navigation/Header'
import useAgent from 'hooks/use-agent'
import useAuth from 'hooks/use-auth'
import theme from 'theme'

function RerunAttackLink({ attack }) {
  const to = {
    pathname: '/attacks/new/applications',
    state: { application: attack }
  }
  return (
    <Link css={{ marginLeft: 'auto' }} to={to}>
      <Button>Rerun Attack</Button>
    </Link>
  )
}

RerunAttackLink.propTypes = {
  attack: PropTypes.object
}

export default function AlfiAttackDetailsPage({ match }) {
  const { currentTeamId } = useAuth()
  const guid = match.params?.id ?? ''
  const [isAttackInTerminalState, setIsAttackInTerminalState] = useState(false)

  const {
    data: [
      alfiTrafficQueryMeta,
      alfiApplicationQueryMeta,
      alfiTrafficImpactMeta
    ],
    isLoading: alfiMetadataLoading
  } = useAgent(
    [
      'alfi/metadata/trafficQuery',
      'alfi/metadata/applicationQuery',
      'alfi/metadata/trafficImpact'
    ],
    [[{ fields: [] }], [{ fields: [] }], [{ fields: [] }]],
    { shouldCache: true, interval: 1000 * 60 * 10 }
  )

  const alfiMetadata = {
    applicationQuery: alfiApplicationQueryMeta,
    trafficImpact: alfiTrafficImpactMeta,
    trafficQuery: alfiTrafficQueryMeta
  }

  const {
    data: attack,
    isLoading,
    hasError,
    doFetch
  } = useAgent(
    `/experiments/${guid}?teamId=${currentTeamId}`,
    { guid: '', service_states: [] },
    { shouldCache: true, interval: 10000, skipRequest: isAttackInTerminalState }
  )

  const { service_states } = attack
  const latestServiceState = service_states[service_states.length - 1]?.stage

  useEffect(() => {
    if (!isAttackInTerminalState && isTerminalState(latestServiceState)) {
      setIsAttackInTerminalState(true)
    } else if (attack.guid && attack.guid !== guid) {
      setIsAttackInTerminalState(false)
    }
  }, [attack.guid, isAttackInTerminalState, guid, latestServiceState])

  return (
    <Fragment>
      <Helmet title="Application Attack Details" />
      <Header>
        <HeaderLink to="/attacks/application">Application Attacks</HeaderLink>
        <HeaderRightChevron />
        <Heading>Attack Details</Heading>
      </Header>
      <AttacksLayout hideSubnav>
        <Section
          errorMessage="There was an error fetching application attacks"
          hasError={hasError}
          isLoading={alfiMetadataLoading || isLoading}
        >
          <div
            css={{
              display: 'flex',
              alignItems: 'center',
              paddingTop: theme.space[3],
              paddingBottom: theme.space[3]
            }}
          >
            {isTerminalState(latestServiceState) ? (
              <RerunAttackLink attack={attack} />
            ) : (
              <ApiTrigger
                destructive
                as={Button}
                css={{ marginLeft: 'auto' }}
                disabled={isLoading}
                errorToastMessage="Unable to add datadog integration"
                request={{
                  url: `/experiments/${guid}?teamId=${currentTeamId}`,
                  method: 'DELETE'
                }}
                successToastMessage="Attack successfully halted"
                onSuccess={() => {
                  doFetch()
                }}
              >
                Halt Attack
              </ApiTrigger>
            )}
          </div>
          <AlfiAttack attack={attack} metadata={alfiMetadata} />
        </Section>
      </AttacksLayout>
    </Fragment>
  )
}

AlfiAttackDetailsPage.propTypes = {
  match: PropTypes.object
}
